import type { ChartHistoryFilterType } from "@roman/shared/constants/predictionChart";
import { CHART_HEIGHT } from "@roman/shared/constants/predictionChart";
import type { ChartConfig, ChartDataPoint, Choice } from "@roman/shared/features/chart/types";
import { useRef, useState } from "react";
import { CartesianGrid, LineChart } from "recharts";
import { useChartEventHandlers } from "./hooks/useChartEventHandlers";
import { useChartLines } from "./hooks/useChartLines";
import { useChartContainer } from "./hooks/usePointerEvents";
import { ChartContainer, ChartTooltip } from "./ui/chart";
import { createXAxis } from "./utils/createXAxis";
import { createYAxis } from "./utils/createYAxis";
import { createTooltipContent } from "./utils/tooltipContent";

interface ChartProps {
  chartData: ChartDataPoint[];
  selectedChoices: Choice[];
  selectedChoiceIds: string[];
  chartConfig: ChartConfig;
  displayMode: ChartHistoryFilterType;
  isLineVisible: boolean;
}

export const Chart = ({
  chartData,
  selectedChoices,
  selectedChoiceIds,
  chartConfig,
  displayMode,
  isLineVisible,
}: ChartProps) => {
  const [isActiveDotShown, setIsActiveDotShown] = useState(false);

  // The official type definition for ref in recharts' LineChart doesn't exist, so using any
  // https://github.com/recharts/recharts/issues/2665
  const chartRef = useRef<any>(null);

  const chartLines = useChartLines({
    selectedChoices,
    chartConfig,
    chartData,
    isActiveDotShown,
  });

  const { className } = useChartContainer({ isLineVisible });

  const { handleTouchEndCancel, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave } =
    useChartEventHandlers({
      setIsActiveDotShown,
      chartRef,
      isLineVisible,
    });

  return (
    <div className={className}>
      <ChartContainer
        config={chartConfig}
        className="w-full !justify-start touch-pan-y"
        onTouchEnd={handleTouchEndCancel}
        onTouchCancel={handleTouchEndCancel}
        style={{ height: CHART_HEIGHT }}
      >
        <LineChart
          data={isLineVisible ? chartData : []}
          key={isLineVisible ? "loaded" : "initial"}
          margin={{ left: 20, right: -20, top: 20, bottom: 0 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          ref={chartRef}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {createXAxis({ chartData, displayMode })}
          {createYAxis({ chartData, selectedChoiceIds })}
          <ChartTooltip
            content={createTooltipContent}
            allowEscapeViewBox={{ x: false, y: false }}
            isAnimationActive={false}
            trigger="hover"
          />
          {chartLines}
        </LineChart>
      </ChartContainer>
    </div>
  );
};
